import React, { useCallback, useContext, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { allCountries } from 'country-telephone-data'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Flag from 'LuxKit/Flag'
import { getPrefixCountry } from 'lib/geo/getPrefixCountry'
import noop from 'lib/function/noop'
import PhonePrefixItem from './PhonePrefixItem'
import TextInputOnly from '../TextInputOnly'
import InputLabelWrap from '../InputLabelWrap'
import config from 'constants/config'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import InputText from 'LuxKit/Typography/InputText'
import HiddenInput from '../HiddenInput'
import PhoneInputPrefixModal from './PhoneInputPrefixModal'
import TextInput from '../TextInput'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { sortBy } from 'lib/array/arrayUtils'
import { ISO2_START_ORDER } from 'constants/config/region'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'

const PhoneNumberInput = styled(TextInputOnly)`
  && > input {
    padding-left: ${rem(104)};
  }
`

const PrefixDropdown = styled(Clickable)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};

  &:focus {
    ${interactiveFocusStyle}
  }
`

export interface PhonePrefixCountry {
  dialCode: string;
  format: string;
  iso2: string;
  name: string;
  priority: number;
}

interface Props {
  defaultPhoneNumber?: string;
  defaultPhonePrefix?: string;
  phoneNumber?: string;
  /**
   * Note: For AU numbers, the event is synthesized with only e.target.value modified.
   * Avoid accessing other event properties like e.target.name or calling e.target.focus().
   */
  onNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPrefixChange?: (data: { phonePrefix: string, code: string }) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  prefixCountry?: string;
  phonePrefix?: string;
  placeholder?: string;
  inputRef?: any;
  name?: string;
  required?: boolean;
  requiredErrorMessage?: string;
  inputTestId?: string;
  id?: string;
  noValidationSpacing?: boolean;
  label?: string;
  className?: string;
  autoComplete?: string;
  phonePrefixOptions?: Array<PhonePrefixCountry>;
  disabled?: boolean;
  /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the prefix name to a flat string
   */
  prefixNameOverride?: string;
  /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the phone name to a flat string
   */
  phoneNameOverride?: string;
  helpText?: string;
  hidden?: boolean;
}

function PhoneInput(props: Props) {
  const {
    autoComplete = 'tel-national',
    defaultPhoneNumber,
    defaultPhonePrefix,
    phoneNumber,
    onBlur = noop,
    onFocus = noop,
    prefixCountry,
    phonePrefix,
    placeholder = 'Phone',
    inputRef,
    required,
    requiredErrorMessage,
    name,
    onNumberChange = noop,
    inputTestId,
    id: propsId,
    onPrefixChange = noop,
    label,
    noValidationSpacing,
    className,
    phonePrefixOptions,
    disabled,
    prefixNameOverride,
    phoneNameOverride,
    helpText,
    hidden,
  } = props

  const prefixTriggerRef = useRef<HTMLButtonElement>(null)
  const prefixSearchRef = useRef<HTMLInputElement>(null)
  const [isPrefixDropdownOpen, togglePrefixDropdown, , closePrefixDropdown] = useToggle()
  const [localPrefix, setLocalPrefix] = useState<string | undefined>(phonePrefix ?? defaultPhonePrefix)
  const [localCountryCode, setLocalCountryCode] = useState<string | undefined>(prefixCountry)
  const [prefixTypeahead, setPrefixTypeahead] = useState<string | undefined>()
  const [localNumber, setLocalNumber] = useState<string | undefined>(phoneNumber ?? defaultPhoneNumber ?? '')

  const formId = useId()
  const id = propsId ?? formId

  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)

  const onPrefixSelect = useCallback((country: PhonePrefixCountry) => {
    // Clear any pending validation timeout to avoid stale country validation
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
      validationTimeoutRef.current = null
    }
    setLocalPrefix(country.dialCode)
    setLocalCountryCode(country.iso2.toUpperCase())
    setPrefixTypeahead(undefined)
    setLocalNumber('')
    onPrefixChange({
      phonePrefix: country.dialCode,
      code: country.iso2.toUpperCase(),
    })
    closePrefixDropdown()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPrefixChange])

  const allowedCountries = useMemo<Array<PhonePrefixCountry>>(() => {
    if (phonePrefixOptions) {
      return phonePrefixOptions
    }
    if (config.restrictPhoneNumbersToSupportedRegions) {
      const allowedCodes = new Set(config.regions.map(region => region.code))
      return allCountries.filter((country: PhonePrefixCountry) => (allowedCodes.has(country.iso2.toUpperCase())))
    }
    return allCountries
  }, [phonePrefixOptions])

  const onPrefixToggle = useCallback(async() => {
    if (isMobile) {
      const nextCountry = await showModal<PhonePrefixCountry | undefined>(<PhoneInputPrefixModal
        countries={allowedCountries}
        selectedCountryCode={localCountryCode || prefixCountry}
      />,
      )
      if (nextCountry) {
        onPrefixSelect(nextCountry)
      }
    } else {
      togglePrefixDropdown()
    }
  }, [allowedCountries, isMobile, localCountryCode, onPrefixSelect, prefixCountry, showModal, togglePrefixDropdown])

  const country = useMemo(() => {
    const effectiveCountryCode = localCountryCode ?? prefixCountry ?? ''
    const effectivePrefix = localPrefix ?? phonePrefix ?? ''
    return getPrefixCountry(effectiveCountryCode, effectivePrefix)
  }, [localCountryCode, localPrefix, phonePrefix, prefixCountry])

  const deferredTypehead = useDeferredValue(prefixTypeahead)
  const hiddenPrefixes = useMemo(() => {
    if (deferredTypehead) {
      const sanitizedPrefix = deferredTypehead.replace('+', '')
      const hiddenCountries = allowedCountries.filter(option =>
        !option.name.toLowerCase().includes(sanitizedPrefix.toLowerCase()) &&
        !option.dialCode.startsWith(sanitizedPrefix),
      )
      return new Set(hiddenCountries)
    }
    return new Set()
  }, [allowedCountries, deferredTypehead])

  const allowedCountriesSorted = useMemo(() => {
    return sortBy(allowedCountries, (country) => {
      const regionOrder = ISO2_START_ORDER.indexOf(country.iso2)
      if (regionOrder === -1) {
        // Put it at the bottom of the list if it's not in the desired order
        return 999
      }
      return regionOrder
    }, 'asc')
  }, [allowedCountries])

  const onPrefixDropdownClose = useCallback(() => {
    setPrefixTypeahead(undefined)
    closePrefixDropdown()
  }, [closePrefixDropdown])

  useEffect(() => {
    if (isPrefixDropdownOpen) {
      prefixSearchRef.current?.focus()
    }
  }, [isPrefixDropdownOpen])

  // Format AU phone numbers with spaces: 0412 345 678 or 412 345 678
  const formatAUPhoneNumber = useCallback((value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '')

    if (digits.length === 0) return ''

    // Format: XXXX XXX XXX for 10 digits (with leading 0) or XXX XXX XXX for 9 digits
    const offset = digits.startsWith('0') ? 1 : 0
    if (digits.length <= 3 + offset) return digits
    if (digits.length <= 6 + offset) return `${digits.slice(0, 3 + offset)} ${digits.slice(3 + offset)}`
    return `${digits.slice(0, 3 + offset)} ${digits.slice(3 + offset, 6 + offset)} ${digits.slice(6 + offset, 9 + offset)}`
  }, [])

  // Format pre-filled/initial value for AU on mount or when country changes to AU
  useEffect(() => {
    const trimmed = localNumber?.trim()
    // Only format if it's unformatted (digits only, no spaces)
    if (country.prefixCountry === 'AU' && trimmed && /^\d+$/.test(trimmed)) {
      setLocalNumber(formatAUPhoneNumber(trimmed))
    }
  }, [country.prefixCountry, formatAUPhoneNumber]) // eslint-disable-line react-hooks/exhaustive-deps

  // Get the raw phone number (without spaces) for form submission
  const rawPhoneNumber = useMemo(() => {
    const displayValue = phoneNumber ?? localNumber ?? ''
    return displayValue.replace(/\s/g, '')
  }, [phoneNumber, localNumber])

  // Ref for debounced validation timeout
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Ref to store the input element for debounced validation
  const phoneInputElementRef = useRef<HTMLInputElement | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  const onPhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = e.currentTarget.value

    if (!/^[0-9\s]*$/.test(currentValue)) {
      e.preventDefault()
      return
    }

    // Store ref to input element for debounced validation
    // Safe because we're using the actual DOM element, not the synthetic event
    phoneInputElementRef.current = e.currentTarget

    // Get raw digits for validation
    const rawDigits = currentValue.replace(/\s/g, '')

    // Clear any pending validation timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    // Clear validation immediately while typing
    e.currentTarget.setCustomValidity('')

    // Auto-format for AU users (only affects display, not the callback value)
    if (country.prefixCountry === 'AU') {
      const formattedNumber = formatAUPhoneNumber(currentValue)
      setLocalNumber(formattedNumber)

      // Pass raw value (without spaces) to parent callback to avoid breaking existing consumers
      // e.g. AccountAccessUserInput uses regex that doesn't allow spaces
      // Note: This synthetic event only reliably provides .value - other DOM properties/methods won't work
      const syntheticEvent = {
        ...e,
        currentTarget: { ...e.currentTarget, value: rawDigits },
        target: { ...e.target, value: rawDigits },
      } as React.ChangeEvent<HTMLInputElement>
      onNumberChange(syntheticEvent)
    } else {
      setLocalNumber(currentValue)
      onNumberChange(e)
    }

    // Debounced validation for all countries (1s after user stops typing)
    const currentCountry = country.prefixCountry
    validationTimeoutRef.current = setTimeout(() => {
      if (!phoneInputElementRef.current) return

      // Get current raw digits from the input at the time of validation (not from closure)
      const currentRawDigits = phoneInputElementRef.current.value.replace(/\s/g, '')

      // AU-specific validation: 9 or 10 digits, starting with 04/05 or 4/5
      // @todo: Consider using a library like libphonenumber-js or react-phone-number-input once we need validation for other countries
      if (currentCountry === 'AU') {
        const isInvalidLength = currentRawDigits.length !== 9 && currentRawDigits.length !== 10
        const isInvalid10Digit = currentRawDigits.length === 10 && !currentRawDigits.startsWith('04') && !currentRawDigits.startsWith('05')
        const isInvalid9Digit = currentRawDigits.length === 9 && !currentRawDigits.startsWith('4') && !currentRawDigits.startsWith('5')

        if (currentRawDigits.length > 1 && (isInvalidLength || isInvalid10Digit || isInvalid9Digit)) {
          phoneInputElementRef.current.setCustomValidity('Please enter an Australian mobile phone number or change to your region.')
          // Trigger invalid event without browser popup (unlike reportValidity)
          phoneInputElementRef.current.checkValidity()
        } else {
          phoneInputElementRef.current.setCustomValidity('')
        }
      } else {
        phoneInputElementRef.current.setCustomValidity('')
      }
    }, 1000)
  }, [setLocalNumber, country, onNumberChange, formatAUPhoneNumber])

  const onHiddenCountryCodeChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    // while users can't type into this hidden field - auto complete can fill it in
    // so we get a change from auto-complete, make sure we handle it
    const dialCode = e.currentTarget.value.toLowerCase()
    const country = allowedCountries.find(country => country.dialCode === dialCode)
    if (country) {
      onPrefixSelect(country)
    }
  }, [allowedCountries, onPrefixSelect])

  return (<>
    <InputLabelWrap
      label={label}
      htmlFor={id}
      className={className}
      required={required}
      hidden={hidden}
    >
      {/* Hidden input for form submission - contains phone number without spaces */}
      <HiddenInput
        type="text"
        disabled={disabled}
        name={phoneNameOverride ?? `${name}.phone`}
        value={rawPhoneNumber}
        required={required}
      />
      <PhoneNumberInput
        startIcon={
          <PrefixDropdown
            ref={prefixTriggerRef}
            onClick={onPrefixToggle}
            disabled={disabled}
          >
            <HiddenInput
              type="text"
              disabled={disabled}
              name={prefixNameOverride ?? `${name}.prefix`}
              value={country.phonePrefix}
              autoComplete="tel-country-code"
              onChange={onHiddenCountryCodeChange}
              required={required}
            />
            <Flag data-testid={`flag-${country?.prefixCountry}`} countryCode={country?.prefixCountry} />
            <InputText variant="text-regular">{`+${country.phonePrefix}`}</InputText>
            <LineAngleDownIcon size="XS" />
          </PrefixDropdown>
        }
        disabled={disabled}
        maxLength={15}
        minLength={7}
        id={id}
        type="tel"
        placeholder={placeholder}
        onChange={onPhoneNumberChange}
        onBlur={onBlur}
        onFocus={onFocus}
        value={phoneNumber ?? localNumber}
        ref={inputRef}
        required={required}
        requiredErrorMessage={requiredErrorMessage}
        data-testid={inputTestId}
        noValidationSpacing={noValidationSpacing}
        pattern="^[0-9\s]*$"
        autoComplete={autoComplete}
        helpText={helpText}
      />

    </InputLabelWrap>
    <DropdownList
      size="S"
      open={isPrefixDropdownOpen}
      onClose={onPrefixDropdownClose}
      anchorRef={prefixTriggerRef}
      triggerRef={prefixTriggerRef}
      placement="bottom-start"
      headerExtension={<TextInput
        autoComplete="off"
        onChange={(e) => setPrefixTypeahead(e.currentTarget.value)}
        defaultValue={prefixTypeahead}
        endIcon={<LineSearchIcon />}
        placeholder="Search by country or code"
        ref={prefixSearchRef}
        noValidationSpacing
      />}
    >
      {hiddenPrefixes.size === allowedCountries.length && <BodyText variant="medium">No matching dial codes</BodyText>}
      {allowedCountriesSorted.map(country => <PhonePrefixItem
        size="medium"
        key={country.iso2}
        selected={localCountryCode === country.iso2.toLocaleUpperCase()}
        country={country}
        onSelect={onPrefixSelect}
        hidden={hiddenPrefixes.has(country)}
      />)}
    </DropdownList>
  </>)
}

export default PhoneInput
